'use client';

import { ChangeEvent, FC, useRef } from 'react';

import classNames from 'classnames';
import { Button, Icon, IconSvg, Image, Spinner } from '@/components';
import { ProgressBar } from '../ProgressBar/ProgressBar';

import {
  FILE_SIZE_ERROR_THRESHOLD_VALUE,
  RESIZE_THRESHOLD,
  UPLOAD_ERROR_INVALID_FORMAT,
  UPLOAD_ERROR_MAX_SIZE_EXCEEDED,
} from './constants';
import { useIsMobile } from '@/hooks';

import style from './pictureuploader.module.scss';

interface PictureUploaderProps {
  alt?: string;
  isLoading?: boolean;
  rounded?: boolean;
  title: string;
  imageSrc?: string;
  progress?: number;
  isUploadSequence?: boolean;
  onChange: (_args: { file?: File; error?: string }) => void;
}

const PictureUploader: FC<PictureUploaderProps> = ({
  imageSrc,
  isLoading: isLoadingProp,
  onChange,
  title,
  alt,
  rounded,
  progress,
  isUploadSequence = false,
}) => {
  const loading = isLoadingProp || isUploadSequence;

  const handleFileUpload = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = (e.target.files ?? [])[0];

    if (file) {
      const validFileTypes = [
        'image/jpeg',
        'image/jpg',
        'image/png',
        'image/heif',
        'image/heic',
      ];

      const specialsExtensions = ['.heic', '.heif'];

      const hasValidType = validFileTypes.includes(file.type);

      const hasSpecialExtension = specialsExtensions?.some(ext =>
        file?.name?.toLowerCase()?.endsWith(ext),
      );

      // If it's not a supported format, show an error and reset the input
      if (!hasValidType && !hasSpecialExtension) {
        onChange({ error: UPLOAD_ERROR_INVALID_FORMAT });
        e.target.value = '';
        return;
      }

      let finalFile = file;

      // If it's a HEIC/HEIF file, convert it to JPEG first
      if (hasSpecialExtension) {
        try {
          const heic2any = (await import('heic2any')).default;

          if (typeof heic2any !== 'function') {
            throw new Error(
              'heic2any module could not be loaded or is not a function.',
            );
          }

          const conversionResult = await heic2any({
            blob: file,
            toType: 'image/jpeg',
            quality: 0.9,
          });

          // heic2any can return a single Blob or an array of Blobs.
          // Normalize it to always use a single Blob.
          const convertedBlob = Array.isArray(conversionResult)
            ? conversionResult[0]
            : conversionResult;

          // Create a new File object from the converted Blob
          finalFile = new File(
            [convertedBlob],
            file.name.replace(/\.(heic|heif)$/i, '.jpeg'),
            {
              type: 'image/jpeg',
            },
          );
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Error converting HEIC/HEIF to JPEG:', error);
          onChange({ error: UPLOAD_ERROR_INVALID_FORMAT });
          e.target.value = '';
          return;
        }
      }

      // Check if the file exceeds the maximum allowed size
      if (finalFile.size > FILE_SIZE_ERROR_THRESHOLD_VALUE) {
        onChange({ error: UPLOAD_ERROR_MAX_SIZE_EXCEEDED });
        e.target.value = '';
        return;
      }

      // If the file size exceeds the resize threshold, resize it
      if (finalFile.size > RESIZE_THRESHOLD) {
        resizeImageFile(finalFile, resizedFile => {
          if (resizedFile) {
            onChange({ file: resizedFile });
          } else {
            // If resizing fails, show an error and reset the input
            onChange({ error: UPLOAD_ERROR_MAX_SIZE_EXCEEDED });
            e.target.value = '';
            return;
          }
        });
      } else {
        // If the file size is okay, upload it directly
        onChange({ file: finalFile });
      }
    }
  };

  const resizeImageFile = (
    file: File,
    callback: (_resizedFile: File | null) => void,
  ) => {
    const img = document.createElement('img');
    img.src = URL.createObjectURL(file);

    img.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        // eslint-disable-next-line no-console
        console.error('Failed to get canvas context');
        callback(null);
        return;
      }

      let width = img.width;
      let height = img.height;

      // Define a scaling factor and initial quality
      let quality = 0.8;
      let blob;

      const compressAndResize = () => {
        // Calculate the scaling factor
        const scaleFactor = Math.sqrt(RESIZE_THRESHOLD / file.size) * 0.9;
        width = Math.round(width * scaleFactor);
        height = Math.round(height * scaleFactor);

        // Resize the canvas and draw the image
        canvas.width = width;
        canvas.height = height;
        ctx.clearRect(0, 0, width, height);
        ctx.drawImage(img, 0, 0, width, height);

        // Convert canvas to dataURL with current quality
        const dataUrl = canvas.toDataURL(file.type, quality);

        // Convert dataURL to Blob
        const byteString = atob(dataUrl.split(',')[1]);
        const mimeString = dataUrl.split(',')[0].split(':')[1].split(';')[0];
        const arrayBuffer = new Uint8Array(byteString.length);

        for (let i = 0; i < byteString.length; i++) {
          arrayBuffer[i] = byteString.charCodeAt(i);
        }

        blob = new Blob([arrayBuffer], { type: mimeString });

        // Check the blob size
        if (blob.size <= RESIZE_THRESHOLD || quality <= 0.4) {
          // If size is acceptable or quality is too low, return the result
          const resizedFile = new File([blob], file.name, { type: file.type });
          callback(resizedFile);
        } else {
          // Reduce quality and try again
          quality -= 0.1;
          compressAndResize();
        }
      };

      compressAndResize();
    };

    img.onerror = () => {
      // eslint-disable-next-line no-console
      console.error('Failed to load image');
      callback(null);
    };
  };

  const isUploading = !!progress && progress > 0;
  const isCompleted = progress === 100;
  const showImage = !(loading || isUploading) && !!imageSrc;

  const Loading = () => {
    if (isUploadSequence) {
      return (
        <div className={style.uploading_progress}>
          {progress === 100 ? (
            <IconSvg name="check" />
          ) : (
            <IconSvg name="arrow-bracket-up" />
          )}
          <p className={style.uploading_progress_percentage}>
            {Math.round(progress || 0)}%
          </p>
          <ProgressBar
            className={style.uploading_progress_bar}
            progress={progress || 0}
            showPercentage={false}
          />
          {progress === 100 ? 'Upload completed!' : 'Uploading...'}
        </div>
      );
    } else if (isUploading) {
      return (
        <div className={style.uploading_progress}>
          {isCompleted ? (
            <IconSvg name="check" />
          ) : (
            <IconSvg name="arrow-bracket-up" />
          )}
          {!isCompleted && (
            <>
              <p className={style.uploading_progress_percentage}>
                {Math.round(progress)}%
              </p>
              <ProgressBar
                className={style.uploading_progress_bar}
                progress={progress}
                showPercentage={false}
              />
            </>
          )}
          {isCompleted ? 'Upload completed!' : 'Uploading...'}
        </div>
      );
    } else if (loading && !isUploading) {
      return (
        <Spinner className={style.spinner_photo} sm>
          Loading
        </Spinner>
      );
    }
    return null;
  };

  return (
    <div className={style.account__photo}>
      <p
        className={style.account__photo_title}
        dangerouslySetInnerHTML={{
          __html: title,
        }}
      />
      <div
        className={classNames(style.account__photo_container, {
          [style.account__photo_container_borderless]: showImage,
        })}
      >
        <Loading />
        <AccountPhotoDesktop
          alt={alt}
          handleFileUpload={handleFileUpload}
          isLoading={loading || isUploading}
          rounded={rounded}
          src={imageSrc}
        />
        <AccountPhotoMobile
          alt={alt}
          handleFileUpload={handleFileUpload}
          isLoading={loading || isUploading}
          rounded={rounded}
          src={imageSrc}
        />
      </div>
    </div>
  );
};

const AccountPhotoDesktop: FC<IAccountPhotoRenderProps> = ({
  isLoading,
  src,
  alt,
  rounded,
  handleFileUpload,
}) => {
  const { isMobile } = useIsMobile();

  if (isMobile || isLoading) return null;
  const showImage = !!src;

  return (
    <>
      {showImage && (
        <figure
          className={classNames(style.account__documment_img, {
            [style.account__documment_img_perfil_rounded]: rounded,
          })}
        >
          <Image
            alt={alt ?? 'Profile image'}
            fill
            sizes="(min-width: 576px) 176px, 50wv"
            src={src}
            style={{ objectFit: rounded ? 'cover' : 'cover' }}
          />
        </figure>
      )}

      <label
        className={classNames(style.account__document_input_upload, {
          [style.account__document_input_upload_perfil]: rounded,
          [style.account__document_input_loading]: isLoading,
          [style.account__document_input_upload_borderless]: showImage,
        })}
      >
        {showImage && (
          <Icon
            className={classNames(style.account__edit_action, {
              [style.account__edit_action_perfil]: rounded,
            })}
            name="edit"
          />
        )}
        {!src && (
          <span className={style.account__document_input_upload_message}>
            <strong>
              <IconSvg name="upload" />
              Upload the document.
              <span>png,jpg,heif,heic</span>
            </strong>
          </span>
        )}
        <input
          accept=".jpg,.JPG,.jpeg,.JPEG,.png,.PNG,.heic,.HEIC,.heif,.HEIF"
          capture="environment"
          disabled={isLoading}
          onChange={handleFileUpload}
          type="file"
        />
      </label>
    </>
  );
};

const AccountPhotoMobile: FC<IAccountPhotoRenderProps> = ({
  isLoading,
  src,
  alt,
  rounded,
  handleFileUpload,
}) => {
  const { isMobile } = useIsMobile();
  const galleryInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  if (!isMobile || isLoading) return null;

  const showImage = !!src;

  return (
    <>
      <label
        className={classNames(style.account__document_input_upload_mobile, {
          [style.account__document_input_upload_mobile_perfil]: rounded,
          [style.account__document_input_upload_mobile_borderless]: showImage,
        })}
      >
        {!!src && (
          <Icon
            className={classNames(style.account__edit_action, {
              [style.account__edit_action_perfil]: rounded,
            })}
            name="edit"
          />
        )}
        <input
          accept=".jpg,.JPG,.jpeg,.JPEG,.png,.PNG,.heic,.HEIC,.heif,.HEIF"
          disabled={isLoading}
          onChange={handleFileUpload}
          ref={galleryInputRef}
          type="file"
        />
      </label>
      {showImage && (
        <figure
          className={classNames(style.account__documment_img, {
            [style.account__documment_img_perfil_rounded]: rounded,
          })}
        >
          <Image
            alt={alt ?? 'Profile image'}
            fill
            sizes="(min-width: 576px) 176px, 50wv"
            src={src}
            style={{ objectFit: rounded ? 'cover' : 'contain' }}
          />
        </figure>
      )}
      {!src && (
        <div className={style.account__document_input_upload_mobile_content}>
          <div
            className={style.account__document_input_upload_mobile_message}
            onClick={() => galleryInputRef.current?.click()}
            role="button"
            tabIndex={0}
          >
            <IconSvg name="arrow-bracket-up" />
            <span
              className={
                style.account__document_input_upload_mobile_message_title
              }
            >
              Tap to upload photo
            </span>
            <span
              className={
                style.account__document_input_upload_mobile_message_formats
              }
            >
              (png, jpg, heif, heic)
            </span>
          </div>
          <div className={style.account__photo_divider}>
            <span className={style.account__photo_divider_line} />
            <span className={style.account__photo_divider_text}>OR</span>
            <span className={style.account__photo_divider_line} />
          </div>
          <div>
            <Button
              className={style.account__photo_action_button}
              color="primary"
              loading={isLoading}
              onClick={() => cameraInputRef.current?.click()}
              size="sm"
            >
              <div className={style.account__photo_button}>
                <IconSvg name="camera" />
                <span>OPEN CAMERA</span>
              </div>
            </Button>
            <input
              accept=".jpg,.JPG,.jpeg,.JPEG,.png,.PNG,.heic,.HEIC,.heif,.HEIF"
              capture="environment"
              disabled={isLoading}
              onChange={handleFileUpload}
              ref={cameraInputRef}
              style={{ display: 'none' }}
              type="file"
            />
          </div>
        </div>
      )}
    </>
  );
};

interface IAccountPhotoRenderProps {
  alt?: string;
  // eslint-disable-next-line no-unused-vars
  handleFileUpload: (_e: any) => void;
  isLoading?: boolean;
  rounded?: boolean;
  src?: string;
}

export { PictureUploader };
